import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';
import { 
  Maximize2, Minimize2, Volume2, VolumeX, 
  RefreshCw, Wifi, WifiOff, Camera, X
} from 'lucide-react';

/**
 * CameraPreview - Small live preview of a Cloudflare HLS stream.
 * Lets the operator verify camera angle and stream quality inline.
 */
export default function CameraPreview({ playbackUrl, courtName, onClose }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const retryCountRef = useRef(0);
  const [status, setStatus] = useState(playbackUrl ? 'connecting' : 'offline');
  const [muted, setMuted] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!playbackUrl || !videoRef.current) {
      setStatus('offline');
      return;
    }

    const video = videoRef.current;
    let hls;
    retryCountRef.current = 0;

    const initPlayer = () => {
      setStatus('connecting');

      if (Hls.isSupported()) {
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 6,
          maxBufferLength: 10,
          maxMaxBufferLength: 20,
        });

        hls.loadSource(playbackUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setStatus('live');
          retryCountRef.current = 0;
          video.play().catch(() => {});
        });

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              setStatus('offline');
              retryCountRef.current += 1;
              // Stop auto-retrying after 3 attempts
              if (retryCountRef.current < 3) {
                setTimeout(() => {
                  if (hlsRef.current) {
                    hlsRef.current.loadSource(playbackUrl);
                    setStatus('connecting');
                  }
                }, 5000);
              }
            } else {
              setStatus('error');
              hls.destroy();
            }
          }
        });

        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = playbackUrl;
        video.addEventListener('loadedmetadata', () => {
          setStatus('live');
          video.play().catch(() => {});
        });
        video.addEventListener('error', () => setStatus('offline'));
      } else {
        setStatus('error');
      }
    };

    initPlayer();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [playbackUrl]);

  const retry = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    retryCountRef.current = 0;
    setStatus('connecting');
    if (videoRef.current && playbackUrl) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        liveSyncDurationCount: 3,
      });
      hls.loadSource(playbackUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setStatus('live');
        videoRef.current?.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) setStatus('offline');
      });
      hlsRef.current = hls;
    }
  };

  const statusConfig = {
    connecting: { color: 'text-amber-400', bg: 'bg-amber-900/40', label: 'Connecting...', Icon: RefreshCw, animate: true },
    live: { color: 'text-emerald-400', bg: 'bg-emerald-900/40', label: 'LIVE', Icon: Wifi, animate: false },
    offline: { color: 'text-zinc-500', bg: 'bg-zinc-800', label: 'No Signal', Icon: WifiOff, animate: false },
    error: { color: 'text-red-400', bg: 'bg-red-900/40', label: 'Error', Icon: WifiOff, animate: false },
  };

  const { color, bg, label, Icon, animate } = statusConfig[status];

  return (
    <div 
      className={`rounded-xl border border-zinc-700 overflow-hidden transition-all ${
        expanded ? 'fixed inset-4 z-50 bg-black' : 'relative'
      }`}
      data-testid="camera-preview"
    >
      {/* Video */}
      <div className={`relative bg-black ${expanded ? 'h-full' : 'aspect-video'}`}>
        <video
          ref={videoRef}
          muted={muted}
          playsInline
          autoPlay
          className="w-full h-full object-contain"
        />

        {/* Status Overlay (when not live) */}
        {status !== 'live' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
            <Icon className={`w-8 h-8 ${color} mb-2 ${animate ? 'animate-spin' : ''}`} />
            <span className={`text-sm font-medium ${color}`}>{label}</span>
            {(status === 'offline' || status === 'error') && (
              <button
                onClick={retry}
                className="mt-3 px-4 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-300 flex items-center gap-1.5 transition-colors"
                data-testid="camera-preview-retry"
              >
                <RefreshCw className="w-3 h-3" /> Retry
              </button>
            )}
          </div>
        )}

        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-2 py-1.5 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-xs text-zinc-300 font-medium">{courtName || 'Camera'}</span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${bg} ${color}`}>
              <Icon className={`w-2.5 h-2.5 ${animate ? 'animate-spin' : ''}`} />
              {label}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMuted(!muted)}
              className="p-1 rounded hover:bg-white/10 transition-colors"
              data-testid="camera-preview-mute"
            >
              {muted ? (
                <VolumeX className="w-3.5 h-3.5 text-zinc-400" />
              ) : (
                <Volume2 className="w-3.5 h-3.5 text-white" />
              )}
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 rounded hover:bg-white/10 transition-colors"
              data-testid="camera-preview-expand"
            >
              {expanded ? (
                <Minimize2 className="w-3.5 h-3.5 text-zinc-400" />
              ) : (
                <Maximize2 className="w-3.5 h-3.5 text-zinc-400" />
              )}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1 rounded hover:bg-white/10 transition-colors"
                data-testid="camera-preview-close"
              >
                <X className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
